import React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import FlatList from '@/components/FlatList';
import { getNextAPIResultPage } from '@/utils/query';
import LoadingSwitchTransition from '@/components/LoadingSwitchTransition';
import {
  SpinnerInfinite,
  SpinnerWindow,
} from '@/components/Spinner/variations';

import { APIRequestParams, APIResult } from '@/types/Api';
import { Protocolo } from '@/models/Protocolo';
import api from '@/sevices/api';

import InputProtocolo from '../InputProtocolo';

const fetchProtocolos = (
  params: APIRequestParams,
): Promise<APIResult<Protocolo>> =>
  api
    .get<APIResult<Protocolo>>(`/protocolos/`, {
      params,
    })
    .then(res => res.data);

const PageContentProtocolosList: React.FC = () => {
  const {
    data,
    isLoading,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ['protocolos-list'],
    ({ pageParam: page }) => fetchProtocolos({ page }),
    {
      getNextPageParam: getNextAPIResultPage,
    },
  );

  const protocolos = React.useMemo(() => {
    if (data) {
      return data.pages.map(group => group.results).flat();
    }

    return [];
  }, [data]);

  const handleMore = React.useCallback(() => {
    if (!isFetching) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetching]);

  const renderFooter = React.useCallback(() => {
    if (isFetchingNextPage) {
      return <SpinnerInfinite />;
    }

    return null;
  }, [isFetchingNextPage]);

  return (
    <LoadingSwitchTransition
      loading={isLoading}
      style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
      LoadingComponent={<SpinnerWindow>Buscando resultados</SpinnerWindow>}
    >
      <FlatList
        data={protocolos}
        keyExtractor={({ item: { id } }) => id}
        renderItem={({ item }) => <InputProtocolo protocolo={item} />}
        onEndReached={handleMore}
        hasMore={hasNextPage}
        ListFooterComponent={renderFooter}
      />
    </LoadingSwitchTransition>
  );
};

export default PageContentProtocolosList;
